import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Pagination,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    CircularProgress,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    TablePagination,
} from "@mui/material";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import NotesIcon from '@mui/icons-material/Notes';
import axios from "axios";
import debounce from "lodash/debounce";
import PersonIcon from '@mui/icons-material/Person';
import PreferencesModal from "../components/modals/PreferencesModal";
import SmsToPhoneModal from "../components/modals/SmsToPhoneModal";
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EditIcon from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";


// Styles (No changes here)
const StyledTableContainer = styled(TableContainer)({
    backgroundColor: "#202020",
    borderRadius: "12px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
    overflowX: "auto",
});

const CompactTableCell = styled(TableCell)({
    padding: "8px 12px",
    fontSize: "0.875rem",
    color: "#fff",
    whiteSpace: "nowrap",
});

const PaginationBox = styled(Box)({
    display: "flex",
    justifyContent: "center",
    marginTop: "10px",
    "& .MuiPaginationItem-root": {
        color: "#12c099",
    },
});

const FilterFormControl = styled(FormControl)({
    width: '100%',
    backgroundColor: "#303030",
    borderRadius: "8px",
    "& .MuiInputBase-root": {
        color: "#fff",
        fontSize: "0.9rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#12c099",
    },
    "& .MuiInputLabel-root": {
        color: "#12c099",
        fontSize: "0.9rem",
    },
    "& .MuiSelect-icon": {
        color: '#12c099',
    },
});

const SearchBar = styled(TextField)({
    width: "100%",
    backgroundColor: "#303030",
    borderRadius: "8px",
    "& .MuiInputBase-root": {
        color: "#fff",
        fontSize: "0.9rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#12c099",
    },
    "& .MuiInputLabel-root": {
        color: "#12c099",
        fontSize: "0.9rem",
    },
});

const Leads = () => {
    const [leads, setLeads] = useState([]);
    const [page, setPage] = useState(0); // 🔄 MODIFIED: page is now 0-indexed for TablePagination
    const [rowsPerPage, setRowsPerPage] = useState(25); // ✨ NEW: State for rows per page
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [leadToDelete, setLeadToDelete] = useState(null);
    const [totalLeads, setTotalLeads] = useState(0);
    const [isPrefsModalOpen, setIsPrefsModalOpen] = useState(false);
    const [currentPrefs, setCurrentPrefs] = useState(null);
    const [prefsLoading, setPrefsLoading] = useState(false);
    const [prefsError, setPrefsError] = useState('');
    const [notificationModalOpen, setNotificationModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState(null);
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [smsBody, setSmsBody] = useState("");
    const [isNotifying, setIsNotifying] = useState(false);
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [currentLeadForNotes, setCurrentLeadForNotes] = useState(null);
    const [leadNotes, setLeadNotes] = useState("");
    const [contactedFilter, setContactedFilter] = useState('All');
    const [smsModalOpen, setSmsModalOpen] = useState(false);
    const [leadForSms, setLeadForSms] = useState(null);
    const [cityEditOpen, setCityEditOpen] = useState(false);
    const [cityEditLead, setCityEditLead] = useState(null);
    const [cityInput, setCityInput] = useState("");
    const [citySaving, setCitySaving] = useState(false);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleOpenCityEdit = (lead) => {
        setCityEditLead(lead);
        setCityInput(lead.city || "");
        setCityEditOpen(true);
        };
    const handleCloseCityEdit = () => {
        setCityEditOpen(false);
        setCityEditLead(null);
        setCityInput("");
        setCitySaving(false);
        };
    const handleSaveCity = async () => {
        if (!cityEditLead) return;
        const oldLeads = leads;
        const newCity = (cityInput || "").trim();
            setLeads(prev =>
                prev.map(l => (l._id === cityEditLead._id ? { ...l, city: newCity || null } : l))
                );
        setCitySaving(true);
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/leads/${cityEditLead._id}`,
                { city: newCity || null }
                );
            handleCloseCityEdit();
            } catch (err) {
            console.error("Failed to update city:", err);
            alert("Failed to save city. Reverting.");
            setLeads(oldLeads); // revert
            setCitySaving(false);
            }
        };


    const handleOpenSmsModal = (lead) => {
        setLeadForSms(lead);
        setSmsModalOpen(true);
    };

    const handleCloseSmsModal = () => {
        setSmsModalOpen(false);
        setLeadForSms(null);
    };

    const handleSmsSuccess = async (leadId) => {
        setLeads(prev => prev.map(l => l._id === leadId ? { ...l, contacted: 'Yes' } : l));
        try {
            await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadId}`, { contacted: 'Yes' });
        } catch (e) {
            console.error('Persist contacted failed (non-fatal):', e);
        }
    };

    const handleOpenPrefsModal = async (lead) => {
        if (!lead.preferencesSubmitted) {
            setCurrentPrefs(null);
            setPrefsError('This lead has not submitted their preferences yet.');
            setPrefsLoading(false);
            setIsPrefsModalOpen(true);
            return;
        }
        setIsPrefsModalOpen(true);
        setPrefsLoading(true);
        setPrefsError('');
        setCurrentPrefs(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/preferences/by-phone/${encodeURIComponent(lead.phone)}`
            );
            setCurrentPrefs(response.data);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Could not fetch preferences.';
            setPrefsError(errorMessage);
            console.error("Error fetching preferences:", error);
        } finally {
            setPrefsLoading(false);
        }
    };

    const handleClosePrefsModal = () => setIsPrefsModalOpen(false);

    useEffect(() => {
        fetchLeads();
    }, []);

    // ✅ UPDATED to be simpler
    const fetchLeads = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/leads`
            );
            setLeads(response.data);
            setTotalLeads(response.data.length);
            setPage(0);
        } catch (error) {
            console.error("Error fetching leads:", error);
        } finally {
            setLoading(false);
        }
    };

    // ❌ DELETED the entire fetchGeocodedData function

    // ... other handler functions like handleDeleteClick, handleSaveNotes, etc. remain unchanged
    const handleDeleteClick = (lead) => {
        setLeadToDelete(lead);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!leadToDelete) return;
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadToDelete._id}`);
            setLeads((prevLeads) => prevLeads.filter((lead) => lead._id !== leadToDelete._id));
            setTotalLeads((prev) => prev - 1);
            setShowDeleteModal(false);
            setLeadToDelete(null);
        } catch (error) {
            console.error("Error deleting lead:", error);
        }
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setLeadToDelete(null);
    };

    const handleOpenNotificationModal = (lead) => {
        setCurrentLead(lead);
        const firstName = lead.name.split(' ')[0];
        const signupLink = 'https://academy.marsreel.com/redirect.html'; // Define the link once

        setEmailSubject("Regarding your Mars Reel Academy Account");

        // Updated Email Body with a clickable HTML link
        setEmailBody(`Hi ${firstName},\n\nWe noticed you may have had an issue creating your account. We've resolved the problem, and you can now try signing up again by clicking here: <a href="${signupLink}">Create Your Account</a>.\n\nPlease let us know if you have any questions.\n\nThanks,\nThe Mars Reel Academy Team`);

        // Updated SMS Body with the raw link
        setSmsBody(`Hi ${firstName}, we fixed an issue with our signup page. You can try creating your Mars Reel Academy account again now. Sorry for the trouble! Sign up here: ${signupLink}`);

        setNotificationModalOpen(true);
    };

    const handleCloseNotificationModal = () => {
        setNotificationModalOpen(false);
        setCurrentLead(null);
        setEmailSubject("");
        setEmailBody("");
        setSmsBody("");
    };

    const handleSendNotification = async () => {
        if (!currentLead) return;
        setIsNotifying(true);
        try {
            // ✅ MODIFIED: Capture the response from the API call
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${currentLead._id}/notify`, { emailSubject, emailBody, smsBody });

            const updatedLead = response.data.lead; // Extract the updated lead from the response

            // ✨ NEW: Update the local state with the new lead data
            setLeads(prevLeads =>
                prevLeads.map(l => (l._id === updatedLead._id ? updatedLead : l))
            );

            alert('Notification sent successfully!');
            handleCloseNotificationModal();

        } catch (error) {
            console.error("Error sending notification:", error);
            alert('Failed to send notification. Please check the console for details.');
        } finally {
            setIsNotifying(false);
        }
    };

    const handleOpenNotesModal = (lead) => {
        setCurrentLeadForNotes(lead);
        setLeadNotes(lead.notes || "");
        setNotesModalOpen(true);
    };

    const handleCloseNotesModal = () => {
        setNotesModalOpen(false);
        setCurrentLeadForNotes(null);
        setLeadNotes("");
    };

    const handleSaveNotes = async () => {
        if (!currentLeadForNotes) return;
        try {
            const updatedLead = { ...currentLeadForNotes, notes: leadNotes, contacted: 'Yes' };
            setLeads(leads.map(lead => lead._id === currentLeadForNotes._id ? updatedLead : lead));
            handleCloseNotesModal();
            await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${currentLeadForNotes._id}`, { notes: leadNotes, contacted: 'Yes' });
        } catch (error) {
            console.error("Error saving notes:", error);
            setLeads(leads.map(lead => lead._id === currentLeadForNotes._id ? currentLeadForNotes : lead));
            alert('Failed to save notes. Please try again.');
        }
    };

    const handleContactedToggle = async (leadToUpdate) => {
        const newStatus = leadToUpdate.contacted === 'Yes' ? 'No' : 'Yes';
        try {
            const updatedLead = { ...leadToUpdate, contacted: newStatus };
            setLeads(leads.map(l => l._id === leadToUpdate._id ? updatedLead : l));
            await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadToUpdate._id}`, { contacted: newStatus });
        } catch (error) {
            console.error("Error updating contacted status:", error);
            setLeads(leads.map(l => l._id === leadToUpdate._id ? leadToUpdate : l));
            alert('Failed to update status. Please try again.');
        }
    };


    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        debouncedSearch();
    };

    const debouncedSearch = useCallback(debounce(() => { setPage(0); }, 300), []);

    // ✅ UPDATED filtering logic
    const filteredLeads = leads.filter((lead) => {
        const search = searchTerm.toLowerCase();

        // Search filter logic
        const matchesSearch = (
            lead.name.toLowerCase().includes(search) ||
            lead.email.toLowerCase().includes(search) ||
            lead.phone.toLowerCase().includes(search) ||
            lead.zipcode.toLowerCase().includes(search) ||
            (lead.city?.toLowerCase() || "").includes(search) || // Directly check lead property
            (lead.state?.toLowerCase() || "").includes(search) || // Directly check lead property
            (lead.country?.toLowerCase() || "").includes(search) // Directly check lead property
        );

        // Contacted status filter logic
        const matchesContacted =
            contactedFilter === 'All' ||
            lead.contacted === contactedFilter;

        return matchesSearch && matchesContacted;
    });

    const paginatedLeads = filteredLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ color: "#fff" }}>
            <Typography variant="h5" gutterBottom>Leads ({filteredLeads.length + 200})</Typography>
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={8} md={9}>
                    <SearchBar
                        variant="outlined"
                        placeholder="Search by name, email, phone, zip, city..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        label="Search Leads"
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                    <FilterFormControl variant="outlined">
                        <InputLabel>Contacted Status</InputLabel>
                        <Select
                            value={contactedFilter}
                            onChange={(e) => {
                                setContactedFilter(e.target.value);
                                setPage(0);
                            }}
                            label="Contacted Status"
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Yes">Contacted</MenuItem>
                            <MenuItem value="No">Not Contacted</MenuItem>
                        </Select>
                    </FilterFormControl>
                </Grid>
            </Grid>

            <StyledTableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {["Name", "Email", "Phone", "Zip Code", "City", "State", "Country", "Notes", "Contacted", "Actions"].map((header) => (
                                <CompactTableCell key={header}>{header}</CompactTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedLeads.map((lead) => (
                            <TableRow key={lead._id}>
                                <CompactTableCell>
                                    <Typography
                                        component="span"
                                        onClick={() => lead.preferencesSubmitted && handleOpenPrefsModal(lead)}
                                        sx={{
                                            color: lead.preferencesSubmitted ? '#12c099' : 'inherit',
                                            cursor: lead.preferencesSubmitted ? 'pointer' : 'default',
                                            '&:hover': {
                                                textDecoration: lead.preferencesSubmitted ? 'underline' : 'none',
                                            },
                                        }}
                                    >
                                        {lead.name}
                                    </Typography>
                                </CompactTableCell>
                                <CompactTableCell>{lead.email}</CompactTableCell>
                                <CompactTableCell>{lead.phone}</CompactTableCell>
                                <CompactTableCell>{lead.zipcode}</CompactTableCell>
                                <CompactTableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <span>{lead.city || "N/A"}</span>
                                <Tooltip title="Edit city">
                                <IconButton
                                size="small"
                                onClick={() => handleOpenCityEdit(lead)}
                                sx={{ color: "#12c099", ml: 0.5 }}
                                >
                                <EditIcon fontSize="inherit" />
                                </IconButton>
                                </Tooltip>
                                </Box>
                                </CompactTableCell>
                                <CompactTableCell>{lead.state || "N/A"}</CompactTableCell>
                                <CompactTableCell>{lead.country || "N/A"}</CompactTableCell>
                                <CompactTableCell>
                                    <IconButton onClick={() => handleOpenNotesModal(lead)} sx={{ color: "#12c099" }}>
                                        <NotesIcon />
                                    </IconButton>
                                </CompactTableCell>
                                <CompactTableCell>
                                    <Switch
                                        checked={lead.contacted === 'Yes'}
                                        onChange={() => handleContactedToggle(lead)}
                                        color="primary"
                                    />
                                </CompactTableCell>
                                <CompactTableCell>
                                    <IconButton onClick={() => handleOpenNotificationModal(lead)} sx={{ color: "#12c099" }}>
                                        <MailOutlineIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleOpenSmsModal(lead)} sx={{ color: "#12c099" }} title="Send to my phone">
                                        <PhoneIphoneIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDeleteClick(lead)} sx={{ color: "#ff1744" }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </CompactTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* ✨ NEW: Desktop Pagination goes INSIDE the container */}
                <TablePagination
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        color: '#fff',
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            color: '#fff',
                        },
                        '& .MuiSelect-icon': {
                            color: '#12c099',
                        }
                    }}
                    component="div"
                    count={filteredLeads.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                />
            </StyledTableContainer>

            <PaginationBox sx={{ display: { xs: 'flex', md: 'none' } }}>
                <Pagination
                    count={Math.ceil(filteredLeads.length / rowsPerPage)}
                    page={page + 1}
                    onChange={(event, value) => setPage(value - 1)}
                    size="small"
                    siblingCount={0}
                />
            </PaginationBox>



            {/* All Dialogs and Modals remain unchanged */}
            <Dialog open={showDeleteModal} onClose={handleCloseModal}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete {leadToDelete?.name}?</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={notesModalOpen} onClose={handleCloseNotesModal} maxWidth="sm" fullWidth>
                <DialogTitle>Notes for {currentLeadForNotes?.name}</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" label="Notes" type="text" fullWidth multiline rows={8} variant="standard" value={leadNotes} onChange={(e) => setLeadNotes(e.target.value)} placeholder="Add call or email notes here..."/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNotesModal}>Cancel</Button>
                    <Button onClick={handleSaveNotes}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={notificationModalOpen} onClose={handleCloseNotificationModal} maxWidth="sm" fullWidth>
                <DialogTitle>Notify Lead: {currentLead?.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>To: {currentLead?.email} & {currentLead?.phone}</Typography>
                    <TextField autoFocus margin="dense" label="Email Subject" type="text" fullWidth variant="standard" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                    <TextField margin="dense" label="Email Body" type="text" fullWidth multiline rows={6} variant="standard" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
                    <TextField margin="dense" label="SMS Body" type="text" fullWidth multiline rows={3} variant="standard" value={smsBody} onChange={(e) => setSmsBody(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNotificationModal}>Cancel</Button>
                    <Button onClick={handleSendNotification} disabled={isNotifying}>{isNotifying ? <CircularProgress size={24} /> : 'Send Notification'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={cityEditOpen} onClose={handleCloseCityEdit} maxWidth="xs" fullWidth>
                <DialogTitle>Edit City{cityEditLead ? ` — ${cityEditLead.name}` : ""}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="City"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={cityInput}
                        onChange={(e) => setCityInput(e.target.value)}
                        placeholder="Enter city (e.g., Los Angeles)"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        This only updates the <em>city</em> field.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCityEdit} disabled={citySaving}>Cancel</Button>
                    <Button onClick={handleSaveCity} disabled={citySaving} variant="contained">
                        {citySaving ? <CircularProgress size={20} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>


            <PreferencesModal
                open={isPrefsModalOpen}
                onClose={handleClosePrefsModal}
                loading={prefsLoading}
                error={prefsError}
                preferences={currentPrefs}
            />

            <SmsToPhoneModal
                open={smsModalOpen}
                onClose={handleCloseSmsModal}
                lead={leadForSms}
                onSuccess={handleSmsSuccess}
            />

        </Box>
    );
};

export default Leads;
